/**
 * The one browser-session owner for NfcAgent state and the localhost socket.
 * Consumers read this context; no hook opens its own connection or mutates a
 * process-wide claim header.
 */
import React, { createContext, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { getApiClient } from '../api/client.js';
import { useAuthStore } from '../store/authStore.js';
import { useInputsChanged } from '../components/ui/use-inputs-changed.js';
import * as nfc from './client.js';
/**
 * The PICC card the desk last saw, after one reader event. A `cardInserted`
 * without card details (a card lifted while NfcAgent read it, as after a write)
 * is no readable card, so no consumer ever holds a null `card`.
 */
export function nextLastCard(current, event) {
    if (event.event === 'cardInserted')
        return event.card ? { readerId: event.readerId, card: event.card } : null;
    return current?.readerId === event.readerId ? null : current;
}
export const NFC_AGENT_CONFIG_CHANGED_EVENT = 'bms:nfc-agent-config-changed';
export const EDGE_ACCESS_STATE_CHANGED_EVENT = 'bms:edge-access-state-changed';
const disconnectedState = {
    status: 'disconnected',
    health: undefined,
    sam: undefined,
    binding: undefined,
    readers: [],
    lastCard: null,
    claimId: null,
};
const NfcAgentContext = createContext(disconnectedState);
export function NfcAgentProvider({ children, enabled = true }) {
    const siteCode = useAuthStore((state) => state.currentSiteCode);
    const accessToken = useAuthStore((state) => state.token);
    const [claimId, setClaimId] = useState(null);
    const [configRevision, setConfigRevision] = useState(0);
    const [accessRevision, setAccessRevision] = useState(0);
    const [wsStatus, setWsStatus] = useState('closed');
    const [health, setHealth] = useState();
    const [sam, setSam] = useState();
    const [binding, setBinding] = useState();
    const [readers, setReaders] = useState([]);
    const [lastCard, setLastCard] = useState(null);
    const wsRef = useRef(null);
    const reconnectTimer = useRef(undefined);
    useEffect(() => {
        const changed = () => setConfigRevision((current) => current + 1);
        window.addEventListener(NFC_AGENT_CONFIG_CHANGED_EVENT, changed);
        return () => window.removeEventListener(NFC_AGENT_CONFIG_CHANGED_EVENT, changed);
    }, []);
    useEffect(() => {
        const accessChanged = () => setAccessRevision((current) => current + 1);
        window.addEventListener(EDGE_ACCESS_STATE_CHANGED_EVENT, accessChanged);
        return () => window.removeEventListener(EDGE_ACCESS_STATE_CHANGED_EVENT, accessChanged);
    }, []);
    const claimSite = useRef(null);
    useEffect(() => {
        let cancelled = false;
        // A refresh keeps the site's last answer until the new one arrives: clearing it first showed
        // "Reader: SAM not set up for this site" for a moment on every card write (2026-10-22).
        if (!enabled || !siteCode || !accessToken || claimSite.current !== siteCode)
            setClaimId(null);
        claimSite.current = enabled && accessToken ? siteCode ?? null : null;
        if (!enabled || !siteCode || !accessToken)
            return;
        const api = getApiClient();
        api.setSiteCodeGetter(() => useAuthStore.getState().currentSiteCode);
        api.setAccessTokenGetter(() => useAuthStore.getState().token);
        api.setAuthFailureHandler(() => useAuthStore.getState().logout());
        void api.getSamMaterial()
            .then((material) => { if (!cancelled)
            setClaimId(material.claimId); })
            .catch(() => { if (!cancelled)
            setClaimId(null); });
        return () => { cancelled = true; };
    }, [accessRevision, accessToken, enabled, siteCode]);
    // Any change to what the binding depends on clears the last answer while rendering;
    // the effect below then asks again.
    const bindingInputsChanged = useInputsChanged([claimId, sam?.claimError, sam?.claimVerified, sam?.present, sam?.uid]);
    if (bindingInputsChanged && binding !== undefined)
        setBinding(undefined);
    useEffect(() => {
        let cancelled = false;
        if (claimId == null || !sam?.present)
            return;
        void nfc.getSamBindingStatus({ claimId })
            .then((result) => { if (!cancelled)
            setBinding(result); })
            .catch((error) => {
            if (!cancelled) {
                setBinding({
                    claimPresent: false,
                    expectedProvided: true,
                    matches: false,
                    status: 'error',
                    error: error instanceof Error ? error.message : 'Failed to check SAM site binding',
                });
            }
        });
        return () => { cancelled = true; };
    }, [claimId, sam?.claimError, sam?.claimVerified, sam?.present, sam?.uid]);
    useEffect(() => {
        // Declared in the effect so the reconnect timer can name it.
        function connect() {
            if (!enabled)
                return;
            const baseUrl = nfc.getNfcAgentUrl().replace(/^http/, 'ws');
            const ws = new WebSocket(`${baseUrl}/ws/readers`);
            wsRef.current = ws;
            ws.onclose = () => {
                if (wsRef.current !== ws)
                    return;
                setWsStatus('closed');
                reconnectTimer.current = setTimeout(connect, 3000);
            };
            ws.onerror = () => ws.close();
            ws.onmessage = (msg) => {
                try {
                    const event = JSON.parse(msg.data);
                    switch (event.event) {
                        case 'snapshot': {
                            setReaders(event.readers);
                            if (event.health)
                                setHealth(event.health);
                            if (event.sam)
                                setSam(event.sam);
                            const first = event.piccCards?.[0];
                            setLastCard(first?.card ? { readerId: first.readerId, card: first.card } : null);
                            setWsStatus('open');
                            break;
                        }
                        case 'cardInserted':
                            setReaders((current) => current.map((reader) => (reader.id === event.readerId ? { ...reader, cardPresent: true } : reader)));
                            setLastCard(nextLastCard(null, event));
                            break;
                        case 'cardRemoved':
                            setReaders((current) => current.map((reader) => (reader.id === event.readerId ? { ...reader, cardPresent: false } : reader)));
                            setLastCard((current) => nextLastCard(current, event));
                            break;
                    }
                }
                catch {
                    // A malformed event is ignored; the next snapshot remains authoritative.
                }
            };
        }
        connect();
        return () => {
            clearTimeout(reconnectTimer.current);
            const ws = wsRef.current;
            wsRef.current = null;
            ws?.close();
        };
    }, [enabled, configRevision]);
    const status = useMemo(() => {
        if (wsStatus === 'closed')
            return 'disconnected';
        if (readers.some((reader) => reader.unavailableReason === 'Reader in use by KeyMaster'))
            return 'keymaster';
        if (!sam?.present)
            return 'no-sam';
        if (claimId == null || binding?.matches === false || sam.claimVerified === false || sam.claimError) {
            return 'warning';
        }
        return 'connected';
    }, [binding, claimId, readers, sam, wsStatus]);
    const value = useMemo(() => ({
        status,
        health,
        sam,
        binding,
        readers,
        lastCard,
        claimId,
    }), [binding, claimId, health, lastCard, readers, sam, status]);
    return React.createElement(NfcAgentContext.Provider, { value }, children);
}
/** Consume the provider-owned agent session. This hook never opens a socket. */
export function useNfcAgent() {
    return useContext(NfcAgentContext);
}
/** @deprecated Use useNfcAgent() instead. */
export function useNfcAgentHealth() {
    const { status, health, sam } = useNfcAgent();
    return { status, health, sam, isLoading: false };
}
/** @deprecated Use useNfcAgent() instead. */
export function useNfcReaders() {
    const { readers, lastCard, status } = useNfcAgent();
    return { readers, lastCard, connected: status !== 'disconnected' };
}
//# sourceMappingURL=hooks.js.map